/**
 * "They have another call" (Robert, 2026-10-16: "I wonder if we can actually
 * smoothly show 'they have received another call on another service, please
 * hold'"). When a FaceTime, phone or other call comes in on an iPhone during
 * a Malka call, iOS puts that call over the app and the camera stops. The
 * other people saw a picture that came and went, with no reason given.
 *
 * The iPhone that gets the other call sees it through CallKit
 * (ios/sdk/src/VRSCallCamera.m, CXCallObserver) and sets the participant
 * property OTHER_CALL_PROPERTY (base/media/otherCall.native.ts). Everyone
 * else's app reads it (base/participants/middleware.ts) and shows one steady
 * sentence on that person's picture (vrs-layout) until the property clears:
 * when the other call ends, or when the person leaves the call.
 */
/** The participant property: 'incoming', 'active', or '' for none. */
export const OTHER_CALL_PROPERTY = 'malkaOtherCall';
/**
 * The other calls on this phone after one CallKit change.
 *
 * Not another call: a call this phone already had when the Malka call
 * started (the ring that was answered for it), and a call this app started
 * (outgoing: the Malka call itself, a phone leg). An incoming call that is
 * not answered yet is 'incoming'; one that connected is 'active'.
 *
 * @param {Object} calls - The other calls so far, by UUID.
 * @param {IOtherCallChange} change - The change.
 * @param {Set<string>} own - The UUIDs that are not another call.
 * @returns {Object} The other calls now, by UUID.
 */
export function applyOtherCallChange(calls, change, own) {
    const uuid = typeof change.uuid === 'string' ? change.uuid.toLowerCase() : '';
    if (!uuid || own.has(uuid) || change.outgoing) {
        return { ...calls };
    }
    const next = { ...calls };
    if (change.ended) {
        delete next[uuid];
    }
    else {
        next[uuid] = change.connected ? 'active' : 'incoming';
    }
    return next;
}
/**
 * What the other people are told: 'active' when any other call is up, else
 * 'incoming' when one rings, else nothing.
 *
 * @param {Object} calls - The other calls, by UUID.
 * @returns {OtherCallStatus}
 */
export function otherCallStatus(calls) {
    const states = Object.values(calls);
    if (states.includes('active')) {
        return 'active';
    }
    return states.includes('incoming') ? 'incoming' : '';
}
/**
 * Reads the property as another phone received it.
 *
 * @param {unknown} value - The participant's malkaOtherCall.
 * @returns {OtherCallStatus}
 */
export function parseOtherCallStatus(value) {
    return value === 'active' || value === 'incoming' ? value : '';
}
/**
 * The sentence on the person's picture.
 *
 * @param {OtherCallStatus} status - The person's status.
 * @param {string} [name] - The person's name.
 * @returns {string} Empty when there is nothing to say.
 */
export function otherCallText(status, name) {
    const who = name?.trim() || 'This person';
    if (status === 'incoming') {
        return `${who} has another call coming in`;
    }
    return status === 'active' ? `${who} is on another call` : '';
}
/**
 * The second, lighter line under the sentence (Robert, 2026-11-02). Only
 * when the person answered the other call ('active'), so you are on hold.
 * Not while it rings ('incoming'): the person did not decide yet, and the
 * screen shows the real state. Three slow dots follow it on the screen;
 * they are decoration only.
 *
 * @param {OtherCallStatus} status - The person's status.
 * @returns {string} Empty when there is no second line.
 */
export function otherCallHint(status) {
    return status === 'active' ? 'Hang tight' : '';
}
/**
 * What a screen reader says for the block: the sentence and, when there is
 * one, the second line, each as its own sentence. The dots are not read.
 *
 * @param {OtherCallStatus} status - The person's status.
 * @param {string} [name] - The person's name.
 * @returns {string} Empty when there is nothing to say.
 */
export function otherCallSpoken(status, name) {
    const text = otherCallText(status, name);
    const hint = otherCallHint(status);
    if (!text) {
        return '';
    }
    return hint ? `${text}. ${hint}.` : text;
}
